/**
 * @file 应用外壳 —— 主菜单 / 建房入座 / 对局屏 的状态机。
 *
 * M4-T2：从「M1 占位页」升级为可玩客户端外壳。React 只做外壳与悬浮层，
 * 对局渲染全部由 PixiJS（`ui/GameScreen` → `pixi/tableScene`）接管。
 */

import { useCallback, useState } from 'react';
import type { AIDifficulty } from '@majiang/core';
import type { GameMode, LocalSettings, OnlineConnInfo } from './session';
import { MenuScreen } from './ui/MenuScreen';
import { RoomScreen } from './ui/RoomScreen';
import { GameScreen } from './ui/GameScreen';

const LS_KEY = 'mj-web-online-session';

interface SavedSession {
  readonly conn: OnlineConnInfo;
}

function loadSaved(): OnlineConnInfo | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as SavedSession;
    if (typeof parsed.conn?.roomCode === 'string' && typeof parsed.conn?.seatToken === 'string') {
      return parsed.conn;
    }
    return null;
  } catch {
    return null;
  }
}

function saveSession(conn: OnlineConnInfo): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ conn } satisfies SavedSession));
  } catch {
    /* 隐私模式等场景忽略 */
  }
}

function clearSaved(): void {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* 忽略 */
  }
}

export function App(): React.JSX.Element {
  const [screen, setScreen] = useState<'menu' | 'room' | 'game'>('menu');
  const [mode, setMode] = useState<GameMode | null>(null);
  const [settings, setSettings] = useState<LocalSettings>({ aiDifficulty: 'NORMAL', rounds: 1 });
  const [conn, setConn] = useState<OnlineConnInfo | null>(null);
  const [gameKey, setGameKey] = useState(0);
  const [saved] = useState<OnlineConnInfo | null>(() => loadSaved());

  const startLocal = useCallback((difficulty: AIDifficulty, rounds: 1 | 8 | 16) => {
    setSettings({ aiDifficulty: difficulty, rounds });
    setMode('local');
    setGameKey((k) => k + 1);
    setScreen('game');
  }, []);

  const enterOnline = useCallback((c: OnlineConnInfo) => {
    setConn(c);
    saveSession(c);
    setMode('online');
    setGameKey((k) => k + 1);
    setScreen('game');
  }, []);

  const backToMenu = useCallback(() => {
    setScreen('menu');
    setMode(null);
    setConn(null);
  }, []);

  if (screen === 'room') {
    return <RoomScreen onBack={backToMenu} onEnterGame={enterOnline} />;
  }

  if (screen === 'game' && mode !== null) {
    if (mode === 'local' && settings !== null) {
      return (
        <GameScreen
          key={gameKey}
          mode="local"
          settings={settings}
          conn={null}
          onExit={() => {
            setScreen('menu');
            setMode(null);
          }}
          onRestart={() => setGameKey((k) => k + 1)}
        />
      );
    }
    if (mode === 'online' && conn !== null) {
      return (
        <GameScreen
          key={gameKey}
          mode="online"
          settings={null}
          conn={conn}
          onExit={() => {
            clearSaved();
            backToMenu();
          }}
          onRestart={backToMenu}
        />
      );
    }
  }

  return (
    <MenuScreen
      onStartLocal={startLocal}
      onEnterRoom={() => setScreen('room')}
      savedConn={saved}
      onResume={(c) => {
        enterOnline(c);
      }}
    />
  );
}
